import React, { useEffect, useState } from "react";
import { Installation, Device, Organization } from "../common/ObjectTypes";
import { toast } from "react-toastify";
import { MoonLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { getInstallations } from "../../api/installations/installationApi";
import { DeviceLookup, searchDevices } from "../../api/devices/deviceApi";
import DeviceList from "./DevicesList";
import { getOrganizations } from "../../api/organizations/organizationsApi";

const ManageDevicesPage = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloadPage, setReloadPage] = useState(true);
  // const [query, setQuery] = useState<string>("");
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [recordStart, setRecordStart] = useState(0);
  const [recordEnd, setRecordEnd] = useState(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [wildXRNumber, setWildXRNumber] = useState<string>("");
  const [lookupError, setLookupError] = useState("");

  const pageSize = 25;
  const [searchQuery, setSearchQuery] = useState({
    name: "",
    pageNumber: 1,
    pageSize,
    // selectVideoCataloguesPage ? pageSize : 25,
  });
  const navigate = useNavigate();
  useEffect(() => {
    const loadDevices = async () => {
      setLoading(true);
      try {
        const temp = await searchDevices(searchQuery);
        setDevices(temp.results);
        loadInstallations();
        loadOrganizations();
        setPageCount(Math.ceil(temp.totalCount / pageSize));
        setTotalCount(temp.totalCount);
        const start = (searchQuery.pageNumber - 1) * searchQuery.pageSize + 1;
        const end = Math.min(start + temp.results.length - 1, temp.totalCount);

        setRecordStart(start);
        setRecordEnd(end);
        setPageLoaded(true);
        setReloadPage(false);
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };

    const loadInstallations = async () => {
      try {
        const temp = await getInstallations(1000);
        setInstallations(temp);
      } catch (err) {
        console.log(err);
      }
    };

    const loadOrganizations = async () => {
      try {
        const temp = await getOrganizations();
        setOrganizations(temp);
      } catch (err) {
        console.log(err);
      }
    };

    loadDevices();
  }, [pageLoaded, reloadPage]); // Updated dependency array to include searchQuery

  const handleOnClickAdd = () => {
    navigate("/installation/add");
  };

  const handlePageClick = (selectedItem: { selected: number }) => {
    setSearchQuery((prevQuery) => ({
      ...prevQuery,
      pageNumber: selectedItem.selected + 1,
    }));
    setReloadPage(true);
  };

  const handleSelectDevice = (id: any) => {
    navigate(`/device/${id}/edit`);
  };

  const handleSearch = async (e: any) => {
    try {
      setSearchQuery((prevQuery) => ({
        ...prevQuery,
        name: searchQuery.name || "",
        pageNumber: 1,
      }));
      setReloadPage(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery((prevQuery: any) => ({
      ...prevQuery,
      name: event.target.value,
    }));
  };

  const handleDelete = async () => {
    try {
      toast.success("Installation was deleted.", {
        position: toast.POSITION.TOP_RIGHT,
        hideProgressBar: true,
        autoClose: 2500,
      });
      setPageLoaded(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setWildXRNumber("");
    setLookupError("");
  };

  const handleChangeLookup = (e: any) => {
    const { value } = e.target;
    setWildXRNumber(value);
  };

  const handleDeviceLookup = async () => {
    setLookupError("");
    setLoading(true);
    if (!wildXRNumber || wildXRNumber.trim() === "") {
      setLookupError("Please enter a valid WildXR Number");
      setLoading(false);
      return;
    }
    const sanitizedInput = wildXRNumber
      .toLowerCase()
      .replaceAll("-", "")
      .trim();
    try {
      const deviceResponse = await DeviceLookup(sanitizedInput);
      if (deviceResponse) {
        navigate(`/device/${deviceResponse.deviceId}/edit`);
      } else {
        setLookupError(`No device found with WildXR Number: ${wildXRNumber}`);
      }
    } catch (error) {
      console.error("Error looking up device:", error);
      setLookupError("An error occurred while searching for this device.");
    } finally {
      setLoading(false);
    }
  };

  return !pageLoaded ? (
    <div className="flex justify-center items-center">
      <MoonLoader color="#9EB45B" />
    </div>
  ) : (
    <DeviceList
      devices={devices}
      installations={installations}
      organizations={organizations}
      onSelectDevice={handleSelectDevice}
      onClickAdd={handleOnClickAdd}
      onDelete={handleDelete}
      onChangeSearch={handleChangeFilter}
      onSearch={handleSearch}
      searchQuery={searchQuery}
      pageCount={pageCount}
      pageSize={pageSize}
      totalCount={totalCount}
      handlePageClick={handlePageClick}
      reloadPage={reloadPage}
      recordStart={recordStart}
      recordEnd={recordEnd}
      loading={loading}
      onOpenDeviceLookupModal={handleOpenModal}
      onCloseDeviceLookupModal={handleCloseModal}
      isModalOpen={isModalOpen}
      onChangeLookup={handleChangeLookup}
      wildXRNumber={wildXRNumber} // Pass the lookup number state to the DeviceList
      lookupError={lookupError} // Pass the lookup error state to the DeviceList
      onDeviceLookup={handleDeviceLookup} // Pass the lookup function to the DeviceList
    />
  );
};

export default ManageDevicesPage;
